/**
 * Shared pill list for the table-cell preview components.
 *
 * All six previews (`DrugInput`, `DrugOutput`, `ProcedureInput`, `ProcedureOutput`,
 * `DiagnosisInput`, `SiteOfCareOutput`) rendered the same thing: a row of compact
 * Badges capped at `maxItems`, followed by a dead `+N more` string. That string was
 * the end of the road — a cell listing 2 of 6 J-codes gave no way to see the other
 * four short of opening the case, and `GenericTable` has no tooltips at all.
 *
 * Here `+N more` is a tooltip trigger listing exactly the pills it is standing in
 * for. `SimpleTooltipV2` is required rather than `SimpleTooltip`: it portals, so it
 * escapes the table's `overflow` clipping. Hover rather than click-to-expand is
 * deliberate — every row is wrapped in a `<Link>` (`GenericTable`), so a click
 * anywhere in the cell navigates to the case and an expander would have to fight it.
 */
import { Badge } from '@components/ui/badge';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import React from 'react';

import { cn } from '@/utils';

export interface PreviewPill {
  /**
   * Semantic identity (a code or name). The list appends the index, so callers
   * need not guarantee uniqueness — two entries sharing a code used to collide
   * into a duplicate React key.
   */
  key: string;
  /** Badge body unless `node` overrides it, and the line shown in the overflow tooltip. */
  text: string;
  /** Richer Badge body for a pill that is not plain text; `text` still feeds the tooltip. */
  node?: React.ReactNode;
  /** Extra Badge classes, e.g. `font-mono` for a billable code. */
  className?: string;
}

interface PillPreviewListProps {
  /** Already-formatted pills. Entries with nothing to show must be filtered out by the caller. */
  pills: PreviewPill[];
  /** How many to render inline before overflowing into the tooltip. */
  maxItems?: number;
}

export const PillPreviewList: React.FC<PillPreviewListProps> = ({ pills, maxItems = 2 }) => {
  if (pills.length === 0) {
    return <span className="text-muted-foreground">--</span>;
  }

  const shown = pills.slice(0, maxItems);
  const hidden = pills.slice(maxItems);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((pill, index) => (
        <Badge
          key={`${pill.key}-${index}`}
          variant="secondary"
          className={cn('text-xs font-normal px-2 py-0.5', pill.className)}
        >
          {pill.node ?? pill.text}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <SimpleTooltipV2
          content={
            // Scrolls rather than truncating: a second "+N more" inside the tooltip
            // would reproduce the dead end this component exists to remove. Radix
            // keeps the content open while the pointer is over it, so it is reachable.
            <div className="flex max-h-60 max-w-xs flex-col gap-1 overflow-y-auto">
              {hidden.map((pill, index) => (
                <span key={`${pill.key}-${index}`} className={pill.className}>
                  {pill.text}
                </span>
              ))}
            </div>
          }
        >
          <span className="text-muted-foreground text-xs underline decoration-dotted underline-offset-2 cursor-help">
            +{hidden.length} more
          </span>
        </SimpleTooltipV2>
      )}
    </div>
  );
};
